/**
 * Client-side profile-photo quality hint (iter84).
 *
 * Runs a handful of cheap Canvas-based heuristics on a picked File before
 * the user submits it. Returns a verdict of `good` or `try_again` with a
 * short human reason. This is ADVISORY only — the server-side Claude Vision
 * check is authoritative. The point of this file is instant feedback so
 * ~90% of obviously-bad uploads never make the round-trip.
 *
 * Checks (all run on a 200x200 downscaled RGBA buffer):
 *   • Min resolution — very small images = webcam pixel dust
 *   • Aspect ratio — reject panoramas / very wide landscapes
 *   • Brightness — mean luminance is not blacked-out or blown-out
 *   • Sharpness — Laplacian-ish edge variance (catches motion blur)
 *   • Center weight — the middle of the frame has more detail than the
 *     corners (crude "subject is centered" hint)
 *
 * We intentionally DON'T do real face detection here — mediapipe/face-api
 * are ~5MB WASM downloads and would double the profile-page bundle. The
 * server-side AI already does the definitive face check.
 */

const DOWNSAMPLE = 200;
const MIN_ORIGINAL = 300;

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
  img.src = url;
});

const luminance = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

export async function checkImageQuality(file) {
  if (!file || !file.type?.startsWith("image/")) {
    return { verdict: "try_again", reason: "Not an image file." };
  }
  let img;
  try {
    img = await loadImage(file);
  } catch {
    return { verdict: "try_again", reason: "Couldn't read the image — try a different file." };
  }

  const { naturalWidth: w, naturalHeight: h } = img;

  if (w < MIN_ORIGINAL || h < MIN_ORIGINAL) {
    return { verdict: "try_again", reason: `Image is only ${w}×${h}px — try a shot at least ${MIN_ORIGINAL}×${MIN_ORIGINAL}px.` };
  }
  const ratio = w / h;
  if (ratio < 0.55 || ratio > 1.9) {
    return { verdict: "try_again", reason: "Very wide or tall crops don't work well — a square or portrait-shaped photo is best." };
  }

  // Downsample onto a canvas for fast pixel analysis.
  const canvas = document.createElement("canvas");
  canvas.width = DOWNSAMPLE;
  canvas.height = DOWNSAMPLE;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    // No canvas — allow upload and let the server AI decide.
    return { verdict: "good", reason: "" };
  }
  ctx.drawImage(img, 0, 0, DOWNSAMPLE, DOWNSAMPLE);
  const { data } = ctx.getImageData(0, 0, DOWNSAMPLE, DOWNSAMPLE);

  // Pass 1: overall brightness stats.
  let lumSum = 0;
  const lumBuf = new Float32Array(DOWNSAMPLE * DOWNSAMPLE);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const L = luminance(data[i], data[i + 1], data[i + 2]);
    lumBuf[p] = L;
    lumSum += L;
  }
  const meanLum = lumSum / (DOWNSAMPLE * DOWNSAMPLE);
  if (meanLum < 30) {
    return { verdict: "try_again", reason: "Photo looks very dark — try somewhere with more light." };
  }
  if (meanLum > 235) {
    return { verdict: "try_again", reason: "Photo looks washed-out — try a spot without direct backlight." };
  }

  // Pass 2: sharpness — mean absolute Laplacian across pixels. Blurry photos
  // have very low edge energy. Also collect center/edge variance for the
  // "subject centered" heuristic (skip the outermost ring so gradient math
  // stays in-bounds).
  let edgeSum = 0;
  let edgeCount = 0;
  let centerVar = 0;
  let cornerVar = 0;
  let centerCount = 0;
  let cornerCount = 0;
  const cLo = Math.floor(DOWNSAMPLE * 0.25);
  const cHi = Math.floor(DOWNSAMPLE * 0.75);
  for (let y = 1; y < DOWNSAMPLE - 1; y += 1) {
    for (let x = 1; x < DOWNSAMPLE - 1; x += 1) {
      const p = y * DOWNSAMPLE + x;
      const lap = Math.abs(
        lumBuf[p] * 4
        - lumBuf[p - 1] - lumBuf[p + 1]
        - lumBuf[p - DOWNSAMPLE] - lumBuf[p + DOWNSAMPLE]
      );
      edgeSum += lap;
      edgeCount += 1;
      if (x >= cLo && x < cHi && y >= cLo && y < cHi) {
        centerVar += lap;
        centerCount += 1;
      } else if (x < cLo / 2 || x > DOWNSAMPLE - cLo / 2 || y < cLo / 2 || y > DOWNSAMPLE - cLo / 2) {
        cornerVar += lap;
        cornerCount += 1;
      }
    }
  }
  const meanEdge = edgeSum / edgeCount;
  if (meanEdge < 4) {
    return { verdict: "try_again", reason: "Photo looks blurry — hold still and try again." };
  }
  // Very high edge energy usually means a screenshot / meme / logo, not a
  // face photo. Threshold is deliberately loose — real faces score < 40.
  if (meanEdge > 60) {
    return { verdict: "try_again", reason: "Looks like a screenshot or graphic — a real photo of you works best." };
  }

  const centerMean = centerCount ? centerVar / centerCount : 0;
  const cornerMean = cornerCount ? cornerVar / cornerCount : 0;
  if (cornerCount && centerMean < cornerMean * 0.6) {
    return { verdict: "try_again", reason: "Your subject looks off-center — try centring your face in the frame." };
  }

  return { verdict: "good", reason: "" };
}
